import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiTrash2, FiRotateCcw, FiAlertCircle, FiFilter, FiRefreshCw } from 'react-icons/fi';
import apiService from '../../services/api';

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 1.5rem;
  }
`;

const Title = styled.h1`
  font-size: clamp(1.5rem, 2.5vw + 1rem, 2rem);
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CleanupBanner = styled.div`
  background: ${props => props.warning ? 'rgba(246, 224, 94, 0.1)' : 'rgba(96, 165, 250, 0.1)'};
  border: 1px solid ${props => props.warning ? 'var(--warning-color)' : 'var(--info-color)'};
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const CleanupInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.warning ? 'var(--warning-dark)' : 'var(--info-dark)'};
  font-size: 0.95rem;
`;

const CleanupButton = styled.button`
  background: ${props => props.warning ? 'var(--warning-color)' : 'var(--info-color)'};
  color: var(--text-inverse);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;
  min-height: 44px;
  touch-action: manipulation;

  &:hover {
    background: ${props => props.warning ? 'var(--warning-dark)' : 'var(--info-dark)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 0.875rem 1rem;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  font-size: 0.95rem;
  min-width: 150px;
  min-height: 44px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  
  @media (max-width: 768px) {
    border: 1px solid var(--border-color);
  }
`;

const Table = styled.table`
  width: 100%;
  
  @media (max-width: 768px) {
    min-width: 600px;
  }
  width: 100%;
  border-collapse: collapse;
  background: var(--background-primary);
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const TableHeader = styled.thead`
  background: var(--primary-color);
  color: var(--text-inverse);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--border-color);
  
  &:hover {
    background: var(--background-secondary);
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.8125rem;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.95rem;
  color: var(--text-primary);
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.8125rem;
  }
`;

const EntityTypeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.85rem;
  font-weight: 500;
  background: ${props => {
    switch(props.type) {
      case 'assignment': return 'rgba(59, 130, 246, 0.1)';
      case 'quiz': return 'rgba(147, 51, 234, 0.1)';
      case 'assignment_submission': return 'rgba(246, 224, 94, 0.1)';
      case 'quiz_submission': return 'rgba(104, 211, 145, 0.1)';
      default: return 'var(--background-tertiary)';
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'assignment': return 'var(--info-color)';
      case 'quiz': return 'var(--accent-color)';
      case 'assignment_submission': return 'var(--warning-color)';
      case 'quiz_submission': return 'var(--success-color)';
      default: return 'var(--text-muted)';
    }
  }};
`;

const ActionButton = styled.button`
  min-height: 44px;
  touch-action: manipulation;
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
  }
  background: ${props => props.variant === 'restore' ? 'var(--success-color)' : 'var(--error-color)'};
  color: var(--text-inverse);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0;
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;

  &:hover {
    background: ${props => props.variant === 'restore' ? 'var(--success-dark)' : 'var(--error-dark)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const ErrorState = styled.div`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  color: var(--error-dark);
  padding: 1rem;
  border-radius: 0;
  margin-bottom: 1rem;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PaginationInfo = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.active {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  margin: 0 0 1rem 0;
  color: var(--text-primary);
`;

const ModalMessage = styled.p`
  margin: 0 0 1.5rem 0;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);

    &:hover {
      background: var(--primary-dark);
    }
  }

  &.secondary {
    background: var(--text-muted);
    color: var(--text-inverse);

    &:hover {
      background: var(--text-secondary);
    }
  }
`;

function DeletedItemsManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    entityType: '',
    page: 1,
    limit: 50
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  });
  const [cleanupStatus, setCleanupStatus] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkRestoring, setBulkRestoring] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkRestoreModal, setShowBulkRestoreModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const fetchDeletedItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.entityType) params.append('entityType', filters.entityType);
      params.append('page', filters.page);
      params.append('limit', filters.limit);

      const response = await apiService.request(`/admin/deleted-items?${params.toString()}`);

      if (response.success) {
        setItems(response.data.items);
        setPagination({
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          totalPages: response.data.totalPages
        });
      } else {
        setError(response.message || 'Failed to fetch deleted items');
      }
    } catch (err) {
      console.error('Error fetching deleted items:', err);
      setError(err.message || 'Failed to fetch deleted items');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchDeletedItems();
    fetchCleanupStatus();
  }, [fetchDeletedItems]);

  const fetchCleanupStatus = async () => {
    try {
      const response = await apiService.request('/admin/deleted-items/cleanup-status');
      if (response.success) {
        setCleanupStatus(response.data);
      }
    } catch (err) {
      console.error('Error fetching cleanup status:', err);
    }
  };

  const handleRestore = async () => {
    if (!selectedItem) return;

    try {
      setRestoring(true);
      const response = await apiService.request(`/admin/deleted-items/${selectedItem.id}/restore`, {
        method: 'POST'
      });

      if (response.success) {
        setShowRestoreModal(false);
        setSelectedItem(null);
        fetchDeletedItems();
        fetchCleanupStatus();
        alert('Item restored successfully!');
      } else {
        alert(`Failed to restore: ${response.message}`);
      }
    } catch (err) {
      console.error('Error restoring item:', err);
      alert(`Failed to restore: ${err.message || 'Unknown error'}`);
    } finally {
      setRestoring(false);
    }
  };

  const handleCleanup = async () => {
    try {
      setCleaningUp(true);
      const response = await apiService.request('/admin/deleted-items/cleanup', {
        method: 'POST'
      });

      if (response.success) {
        setShowCleanupModal(false);
        fetchDeletedItems();
        fetchCleanupStatus();
        alert(`Cleanup completed! ${response.data.deleted} record(s) permanently deleted.`);
      } else {
        alert(`Cleanup failed: ${response.message}`);
      }
    } catch (err) {
      console.error('Error running cleanup:', err);
      alert(`Cleanup failed: ${err.message || 'Unknown error'}`);
    } finally {
      setCleaningUp(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatEntityType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getDaysUntilCleanup = () => {
    if (!cleanupStatus?.oldestRecordDate) return null;
    const oldest = new Date(cleanupStatus.oldestRecordDate);
    const cutoff = new Date(oldest.getTime() + cleanupStatus.retentionDays * 24 * 60 * 60 * 1000);
    const now = new Date();
    const daysLeft = Math.ceil((cutoff - now) / (1000 * 60 * 60 * 24));
    return daysLeft;
  };

  return (
    <Container>
      <Header>
        <Title>
          <FiTrash2 />
          Recently Deleted Items
        </Title>
      </Header>

      {cleanupStatus && (
        <CleanupBanner warning={cleanupStatus.shouldCleanup}>
          <CleanupInfo warning={cleanupStatus.shouldCleanup}>
            <FiAlertCircle />
            <div>
              <strong>Cleanup Status:</strong> {cleanupStatus.totalPending} item(s) pending deletion
              {cleanupStatus.oldestRecordDate && (
                <>
                  <br />
                  Oldest record: {formatDate(cleanupStatus.oldestRecordDate)}
                  {getDaysUntilCleanup() !== null && (
                    <>
                      {' • '}
                      {getDaysUntilCleanup() > 0 ? (
                        <>{getDaysUntilCleanup()} day(s) until cleanup recommended</>
                      ) : (
                        <>Cleanup recommended now</>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </CleanupInfo>
          <CleanupButton
            warning={cleanupStatus.shouldCleanup}
            onClick={() => setShowCleanupModal(true)}
            disabled={cleanupStatus.totalPending === 0}
          >
            <FiRefreshCw />
            Run Cleanup
          </CleanupButton>
        </CleanupBanner>
      )}

      {error && <ErrorState>{error}</ErrorState>}

      <Filters>
        <FilterGroup>
          <FilterLabel>
            <FiFilter /> Entity Type
          </FilterLabel>
          <Select
            value={filters.entityType}
            onChange={(e) => setFilters({ ...filters, entityType: e.target.value, page: 1 })}
          >
            <option value="">All Types</option>
            <option value="assignment">Assignment</option>
            <option value="quiz">Quiz</option>
            <option value="assignment_submission">Assignment Submission</option>
            <option value="quiz_submission">Quiz Submission</option>
          </Select>
        </FilterGroup>
      </Filters>

      {loading ? (
        <LoadingState>Loading deleted items...</LoadingState>
      ) : items.length === 0 ? (
        <EmptyState>
          <FiTrash2 size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
          <p>No deleted items found within the retention period.</p>
        </EmptyState>
      ) : (
        <>
          {/* Bulk Actions Bar */}
          {selectedItems.size > 0 && (
            <div style={{ 
              marginBottom: '1rem', 
              padding: '1rem', 
              background: 'var(--background-secondary)', 
              borderRadius: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div>
                <strong>{selectedItems.size} item(s) selected</strong>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <ActionButton
                  variant="restore"
                  onClick={() => setShowBulkRestoreModal(true)}
                  disabled={bulkRestoring || bulkDeleting}
                >
                  <FiRotateCcw />
                  Bulk Restore ({selectedItems.size})
                </ActionButton>
                <ActionButton
                  variant="delete"
                  onClick={() => setShowBulkDeleteModal(true)}
                  disabled={bulkRestoring || bulkDeleting}
                >
                  <FiTrash2 />
                  Bulk Delete ({selectedItems.size})
                </ActionButton>
                <ActionButton
                  variant="secondary"
                  onClick={() => setSelectedItems(new Set())}
                  disabled={bulkRestoring || bulkDeleting}
                >
                  Clear Selection
                </ActionButton>
              </div>
            </div>
          )}

          <TableWrapper>
            <Table>
              <TableHeader>
              <tr>
                <TableHeaderCell style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedItems.size === items.length && items.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItems(new Set(items.filter(item => !item.restored_at).map(item => item.id)));
                      } else {
                        setSelectedItems(new Set());
                      }
                    }}
                  />
                </TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Title/Summary</TableHeaderCell>
                <TableHeaderCell>Original ID</TableHeaderCell>
                <TableHeaderCell>Deleted At</TableHeaderCell>
                <TableHeaderCell>Deleted By</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </tr>
            </TableHeader>
            <tbody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {!item.restored_at && (
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedItems);
                          if (e.target.checked) {
                            newSelected.add(item.id);
                          } else {
                            newSelected.delete(item.id);
                          }
                          setSelectedItems(newSelected);
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <EntityTypeBadge type={item.entity_type}>
                      {formatEntityType(item.entity_type)}
                    </EntityTypeBadge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <strong>{item.summary?.title || `Item #${item.original_id}`}</strong>
                      {item.summary?.student && (
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                          Student: {item.summary.student}
                        </div>
                      )}
                      {item.summary?.course && (
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                          Course: {item.summary.course}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{item.original_id}</TableCell>
                  <TableCell>{formatDate(item.deleted_at)}</TableCell>
                  <TableCell>
                    {item.deleted_by ? (
                      <div>
                        <div>{item.deleted_by.name}</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                          {item.deleted_by.role} • {item.deleted_by.email}
                        </div>
                      </div>
                    ) : (
                      'Unknown'
                    )}
                  </TableCell>
                  <TableCell>
                    {item.restored_at ? (
                      <span style={{ color: 'var(--success-color)', fontSize: '0.875rem' }}>Restored</span>
                    ) : (
                      <ActionButton
                        variant="restore"
                        onClick={() => {
                          setSelectedItem(item);
                          setShowRestoreModal(true);
                        }}
                      >
                        <FiRotateCcw />
                        Restore
                      </ActionButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
            </Table>
          </TableWrapper>

          <Pagination>
            <PaginationInfo>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} items
            </PaginationInfo>
            <PaginationButtons>
              <PageButton
                onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
              >
                Previous
              </PageButton>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <PageButton
                    key={pageNum}
                    className={pagination.page === pageNum ? 'active' : ''}
                    onClick={() => setFilters({ ...filters, page: pageNum })}
                  >
                    {pageNum}
                  </PageButton>
                );
              })}
              <PageButton
                onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </PageButton>
            </PaginationButtons>
          </Pagination>
        </>
      )}

      {showRestoreModal && selectedItem && (
        <Modal onClick={() => !restoring && setShowRestoreModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Restore Deleted Item</ModalTitle>
            <ModalMessage>
              Are you sure you want to restore this {formatEntityType(selectedItem.entity_type).toLowerCase()}?
              <br /><br />
              <strong>This will restore:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                <li>The {formatEntityType(selectedItem.entity_type).toLowerCase()} itself</li>
                <li>All associated metadata</li>
                <li>All answers, responses, and scores</li>
                <li>All related submissions (if applicable)</li>
              </ul>
            </ModalMessage>
            <ModalButtons>
              <Button
                className="secondary"
                onClick={() => {
                  setShowRestoreModal(false);
                  setSelectedItem(null);
                }}
                disabled={restoring}
              >
                Cancel
              </Button>
              <Button
                className="primary"
                onClick={handleRestore}
                disabled={restoring}
              >
                {restoring ? 'Restoring...' : 'Restore'}
              </Button>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}

      {showCleanupModal && (
        <Modal onClick={() => !cleaningUp && setShowCleanupModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Permanently Delete Old Archives</ModalTitle>
            <ModalMessage>
              This will permanently delete all archive records older than {cleanupStatus?.retentionDays || 30} days.
              <br /><br />
              <strong>This action cannot be undone.</strong> Items past the retention period will no longer be restorable.
              <br /><br />
              Estimated records to delete: {cleanupStatus?.totalPending || 0}
            </ModalMessage>
            <ModalButtons>
              <Button
                className="secondary"
                onClick={() => setShowCleanupModal(false)}
                disabled={cleaningUp}
              >
                Cancel
              </Button>
              <Button
                className="primary"
                onClick={handleCleanup}
                disabled={cleaningUp}
                style={{ background: 'var(--error-color)' }}
              >
                {cleaningUp ? 'Deleting...' : 'Permanently Delete'}
              </Button>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}

      {showBulkRestoreModal && (
        <Modal onClick={() => !bulkRestoring && setShowBulkRestoreModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Bulk Restore Deleted Items</ModalTitle>
            <ModalMessage>
              Are you sure you want to restore {selectedItems.size} selected item(s)?
              <br /><br />
              <strong>This will restore:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                <li>All selected items and their metadata</li>
                <li>All associated answers, responses, and scores</li>
                <li>All related submissions (if applicable)</li>
              </ul>
            </ModalMessage>
            <ModalButtons>
              <Button
                className="secondary"
                onClick={() => {
                  setShowBulkRestoreModal(false);
                }}
                disabled={bulkRestoring}
              >
                Cancel
              </Button>
              <Button
                className="primary"
                onClick={async () => {
                  try {
                    setBulkRestoring(true);
                    const response = await apiService.request('/admin/deleted-items/bulk-restore', {
                      method: 'POST',
                      body: JSON.stringify({ archiveIds: Array.from(selectedItems) })
                    });

                    if (response.success) {
                      setShowBulkRestoreModal(false);
                      setSelectedItems(new Set());
                      fetchDeletedItems();
                      fetchCleanupStatus();
                      alert(`Successfully restored ${response.data.successful} item(s). ${response.data.failed > 0 ? `${response.data.failed} failed.` : ''}`);
                    } else {
                      alert(`Failed to restore: ${response.message}`);
                    }
                  } catch (err) {
                    console.error('Error bulk restoring items:', err);
                    alert(`Failed to restore: ${err.message || 'Unknown error'}`);
                  } finally {
                    setBulkRestoring(false);
                  }
                }}
                disabled={bulkRestoring}
              >
                {bulkRestoring ? 'Restoring...' : `Restore ${selectedItems.size} Item(s)`}
              </Button>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}

      {showBulkDeleteModal && (
        <Modal onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Bulk Permanently Delete Archive Records</ModalTitle>
            <ModalMessage>
              Are you sure you want to permanently delete {selectedItems.size} selected archive record(s)?
              <br /><br />
              <strong style={{ color: 'var(--error-color)' }}>WARNING: This action cannot be undone.</strong>
              <br /><br />
              These items will be permanently removed and cannot be restored.
            </ModalMessage>
            <ModalButtons>
              <Button
                className="secondary"
                onClick={() => {
                  setShowBulkDeleteModal(false);
                }}
                disabled={bulkDeleting}
              >
                Cancel
              </Button>
              <Button
                className="primary"
                onClick={async () => {
                  try {
                    setBulkDeleting(true);
                    const response = await apiService.request('/admin/deleted-items/bulk-delete', {
                      method: 'POST',
                      body: JSON.stringify({ archiveIds: Array.from(selectedItems) })
                    });

                    if (response.success) {
                      setShowBulkDeleteModal(false);
                      setSelectedItems(new Set());
                      fetchDeletedItems();
                      fetchCleanupStatus();
                      alert(`Successfully deleted ${response.data.deleted} archive record(s).`);
                    } else {
                      alert(`Failed to delete: ${response.message}`);
                    }
                  } catch (err) {
                    console.error('Error bulk deleting items:', err);
                    alert(`Failed to delete: ${err.message || 'Unknown error'}`);
                  } finally {
                    setBulkDeleting(false);
                  }
                }}
                disabled={bulkDeleting}
                style={{ background: 'var(--error-color)' }}
              >
                {bulkDeleting ? 'Deleting...' : `Permanently Delete ${selectedItems.size} Item(s)`}
              </Button>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

export default DeletedItemsManagement;
